import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Card, PageHeader, Button, Modal, Input, Select, Textarea, Spinner,
  SearchBar, EmptyState, Pill,
} from "@/components/common";
import { Plus, Pencil, Trash2, Users, UserCircle } from "lucide-react";

const RUOLI = ["socio", "atleta", "genitore", "istruttore", "collaboratore"];
const RUOLO_COLOR = {
  socio: "bg-slate-100 text-slate-600", atleta: "bg-sky-100 text-sky-700",
  genitore: "bg-violet-100 text-violet-700", istruttore: "bg-emerald-100 text-emerald-700",
  collaboratore: "bg-amber-100 text-amber-700",
};
const EMPTY = { nome: "", cognome: "", codice_fiscale: "", data_nascita: "", luogo_nascita: "", email: "", telefono: "", indirizzo: "", contatto_emergenza: "", ruoli: [], stato_socio: "attivo", genitore_di: [], note: "" };

export default function Anagrafica() {
  const { can } = useAuth();
  const editable = can("anagrafica", "edit");
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/persone").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRuolo = (r) => setForm((f) => ({ ...f, ruoli: f.ruoli.includes(r) ? f.ruoli.filter((x) => x !== r) : [...f.ruoli, r] }));
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.nome || !form.cognome) { toast.error("Nome e cognome obbligatori"); return; }
    try {
      const payload = { ...form };
      if (editId) await api.put(`/persone/${editId}`, payload);
      else await api.post("/persone", payload);
      toast.success("Salvato"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare questa persona?")) return; await api.delete(`/persone/${id}`); toast.success("Eliminato"); load(); };

  if (!items) return <Spinner />;
  const filtered = items.filter((p) => `${p.nome} ${p.cognome} ${p.codice_fiscale} ${p.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Anagrafica" subtitle={`${items.length} persone registrate`}
        actions={editable && <Button onClick={openNew} data-testid="add-persona-btn"><Plus size={16} /> Nuova persona</Button>} />
      <div className="mb-4"><SearchBar value={q} onChange={setQ} placeholder="Cerca per nome, CF, email..." /></div>

      {filtered.length === 0 ? <Card className="p-6"><EmptyState icon={Users} title="Nessuna persona" hint="Aggiungi il primo socio o atleta" /></Card> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 bg-slate-50/60 text-left">
                <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider">Persona</th>
                <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider hidden md:table-cell">Codice Fiscale</th>
                <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider hidden lg:table-cell">Contatti</th>
                <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider">Ruoli</th>
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`persona-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-slate-400 shrink-0"><UserCircle size={22} /></div>
                        <div><p className="font-semibold text-slate-800">{p.cognome} {p.nome}</p><p className="text-xs text-slate-400">{p.luogo_nascita} {p.data_nascita && `· ${p.data_nascita}`}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 hidden md:table-cell">{p.codice_fiscale || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 hidden lg:table-cell"><p>{p.email || "—"}</p><p className="text-xs">{p.telefono}</p></td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(p.ruoli || []).map((r) => <Pill key={r} className={RUOLO_COLOR[r]}>{r}</Pill>)}</div></td>
                    <td className="px-4 py-3 text-right">
                      {editable && <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(p)} data-testid={`edit-persona-${p.id}`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={15} /></button>
                        <button onClick={() => del(p.id)} data-testid={`delete-persona-${p.id}`} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica persona" : "Nuova persona"} wide>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Nome" data-testid="persona-nome" value={form.nome} onChange={(e) => upd("nome", e.target.value)} />
            <Input label="Cognome" data-testid="persona-cognome" value={form.cognome} onChange={(e) => upd("cognome", e.target.value)} />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Input label="Codice Fiscale" value={form.codice_fiscale} onChange={(e) => upd("codice_fiscale", e.target.value)} />
            <Input label="Data nascita" type="date" value={form.data_nascita} onChange={(e) => upd("data_nascita", e.target.value)} />
            <Input label="Luogo nascita" value={form.luogo_nascita} onChange={(e) => upd("luogo_nascita", e.target.value)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} />
            <Input label="Telefono" value={form.telefono} onChange={(e) => upd("telefono", e.target.value)} />
          </div>
          <Input label="Indirizzo" value={form.indirizzo} onChange={(e) => upd("indirizzo", e.target.value)} />
          <Input label="Contatto emergenza" value={form.contatto_emergenza} onChange={(e) => upd("contatto_emergenza", e.target.value)} />
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Ruoli</label>
            <div className="flex flex-wrap gap-2">
              {RUOLI.map((r) => (
                <button key={r} type="button" data-testid={`ruolo-${r}`} onClick={() => toggleRuolo(r)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${form.ruoli.includes(r) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300 hover:border-sky-400"}`}>{r}</button>
              ))}
            </div>
          </div>
          {form.ruoli.includes("genitore") && (
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Figli (atleti collegati)</label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {items.filter((x) => x.id !== editId && (x.ruoli || []).includes("atleta")).map((a) => (
                  <button key={a.id} type="button" onClick={() => setForm((f) => ({ ...f, genitore_di: f.genitore_di.includes(a.id) ? f.genitore_di.filter((x) => x !== a.id) : [...f.genitore_di, a.id] }))}
                    className={`px-2.5 py-1 rounded-lg text-xs border ${form.genitore_di.includes(a.id) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-300"}`}>{a.cognome} {a.nome}</button>
                ))}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Select label="Stato socio" value={form.stato_socio} onChange={(e) => upd("stato_socio", e.target.value)}>
              <option value="attivo">Attivo</option><option value="sospeso">Sospeso</option><option value="cessato">Cessato</option>
            </Select>
          </div>
          <Textarea label="Note" rows={2} value={form.note} onChange={(e) => upd("note", e.target.value)} />
          <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-persona-btn">Salva</Button></div>
        </div>
      </Modal>
    </div>
  );
}
